import { Id, Store } from '@nextforce/api'
import { useQueryClient } from '@tanstack/react-query'
import { produce } from 'immer'
import { isNil } from 'lodash'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { StatusBoundary } from 'shared/components/status_boundary'
import { useStoredState } from 'shared/hooks/stored_state'
import { onManualRetry } from 'shared/queries/utils'
import { useStores } from 'stores/queries/list'
import { useUser } from 'users/queries/detail'

export interface RecentStoresContextValue {
  activeStoreId: Id | undefined
  homeStoreId: Id | undefined
  allStoresSelected: boolean | undefined
  setAllStoresSelected?: (selected: boolean) => void
  setActiveStoreId?: (id: Id | undefined) => void
  recentStores: Store[]
}

export const MAX_RECENT_STORES = 8

const RecentStoresContext = createContext({} as RecentStoresContextValue)

interface Props {
  children: React.ReactNode
}
export function RecentStoresProvider(props: Props) {
  const client = useQueryClient()
  const storeQuery = useStores()
  const { data: user } = useUser()

  const [activeId, setActiveId] = useStoredState<Id | undefined>(
    'active_store_id'
  )
  const [homeStoreId, setHomeStoreId] = useState<Id | undefined>()

  const [recentStoreIds, setRecentStoreIds] = useStoredState<
    Id[] | undefined | null
  >('recent_stores')

  const [allStoresSelected, setAllStoresSelected] = useStoredState<boolean>(
    'all_stores_selected'
  )

  const setActiveStoreId = useCallback(
    (storeId: Id | undefined) => {
      setActiveId(storeId)
      if (storeQuery.data) {
        const store = storeQuery.data.stores.find(s => s.id === storeId)
        if (store) {
          addToRecentStores(store)
        }
      }
      function addToRecentStores(recentStore: Store) {
        setRecentStoreIds(
          recentStoreIds
            ? produce(recentStoreIds, updatedRecentStoreIds => {
                const index = updatedRecentStoreIds.findIndex(
                  id => id === recentStore.id
                )
                if (index >= 0) {
                  updatedRecentStoreIds.splice(index, 1)
                }
                updatedRecentStoreIds.push(recentStore.id)
              })
            : [recentStore.id]
        )
      }
    },
    [recentStoreIds, setActiveId, setRecentStoreIds, storeQuery.data]
  )

  function setDefaultActiveStoreId() {
    if (isNil(activeId)) {
      if (storeQuery.data) {
        if (storeQuery.data.homeStoreId) {
          setActiveStoreId(storeQuery.data.homeStoreId)
        } else if (storeQuery.data.stores.length > 0) {
          setActiveStoreId(storeQuery.data.stores[0].id)
        }
      } else if (user?.homeStoreId) {
        setActiveStoreId(user.homeStoreId.toString())
      }
    }
  }

  function setDefaultHomeStoreId() {
    if (!homeStoreId) {
      if (storeQuery.data) {
        if (storeQuery.data.homeStoreId) {
          setHomeStoreId(storeQuery.data.homeStoreId)
        }
      } else if (user?.homeStoreId) {
        setHomeStoreId(user.homeStoreId.toString())
      }
    }
  }

  function seedRecentStores() {
    if (
      storeQuery.data &&
      recentStoreIds !== undefined &&
      !recentStoreIds?.length &&
      storeQuery.data.stores.length > 0
    ) {
      setRecentStoreIds(storeQuery.data.stores.map(s => s.id))
    }
  }

  useEffect(setDefaultHomeStoreId, [
    homeStoreId,
    storeQuery.data,
    user?.homeStoreId,
  ])

  useEffect(setDefaultActiveStoreId, [
    activeId,
    storeQuery.data,
    setActiveStoreId,
    user?.homeStoreId,
  ])

  useEffect(seedRecentStores, [
    storeQuery.data,
    recentStoreIds,
    setRecentStoreIds,
  ])

  useEffect(() => {
    if (isNil(allStoresSelected)) {
      setAllStoresSelected(true)
    }
  }, [allStoresSelected, setAllStoresSelected])

  const recentStores = useMemo(() => {
    if (!storeQuery.data) {
      return []
    }
    let stores: Store[] = []
    const ids = [...(recentStoreIds ?? [])]
      .reverse()
      .slice(0, MAX_RECENT_STORES)
    const recentStoreIdsSet = new Set(ids)
    stores = storeQuery.data.stores.filter(store =>
      recentStoreIdsSet.has(store.id)
    )
    if (stores.findIndex(store => store.id === activeId) === -1) {
      const activeStore = storeQuery.data.stores.find(
        store => store.id === activeId
      )
      if (activeStore) {
        stores.splice(0, 1, activeStore)
      }
    }
    return stores
  }, [activeId, recentStoreIds, storeQuery.data])

  return (
    <StatusBoundary
      isLoading={storeQuery.isLoading}
      error={storeQuery.error}
      onRetry={() => onManualRetry(client)}
    >
      <RecentStoresContext.Provider
        value={{
          activeStoreId: activeId,
          homeStoreId,
          allStoresSelected,
          setAllStoresSelected,
          recentStores,
          setActiveStoreId,
        }}
      >
        {props.children}
      </RecentStoresContext.Provider>
    </StatusBoundary>
  )
}

export function useRecentStoresContext() {
  return useContext<RecentStoresContextValue>(RecentStoresContext)
}
